import type { ComponentType, ReactNode } from "react";
import { Copy, type CopyKey } from "@/copy/copy";

/**
 * What the homepage monitor shows for each tab (DeskPreview). Each screen renders inside an
 * AppFrame (`<div id="app">` in a shadow root), so it is styled by the app's own #app stylesheet.
 *
 * TODO(site/app): the prototype showed the live app here. Once the app's page components can
 * render from fixture data (src/test/fixtures/plan.ts), swap these static illustrations for them,
 * e.g. `dashboard: () => <HomePage plan={fixturePlan} />`. Until then the dashboard is the
 * prototype's dashboard markup with its illustrative household (Alex and Jamie), and the other
 * tabs are a title, a subtitle and unlabelled chart shapes: no figures that aren't the prototype's.
 */
export type DeskKey =
  | "dashboard"
  | "cash-flow"
  | "net-worth"
  | "priorities"
  | "retirement"
  | "debt"
  | "decisions";

function Head({ title, sub }: { title: CopyKey; sub: CopyKey }) {
  return (
    <>
      <Copy as="h1" k={title} />
      <Copy as="p" className="sub" k={sub} />
    </>
  );
}

function Page({ children }: { children: ReactNode }) {
  return <section className="page on">{children}</section>;
}

/** Unlabelled bars, the shape of a chart without its numbers. */
function Bars({ n, color, h = 150 }: { n: number; color: string; h?: number }) {
  return (
    <svg
      viewBox={`0 0 ${n * 20} 100`}
      width="100%"
      height={h}
      preserveAspectRatio="none"
      aria-hidden
    >
      {Array.from({ length: n }, (_, i) => {
        const v = 28 + ((i * 37) % 60);
        return (
          <rect key={i} x={i * 20 + 3} y={100 - v} width={14} height={v} rx={3} fill={color} />
        );
      })}
    </svg>
  );
}

/** Two paths: blue is the current plan, gold is Abi's (the colours carry that meaning). */
function TwoPaths({ h = 220 }: { h?: number }) {
  return (
    <svg viewBox="0 0 600 220" width="100%" height={h} preserveAspectRatio="none" aria-hidden>
      <path
        d="M10,200 C200,190 380,150 590,110"
        stroke="var(--before)"
        strokeWidth="3"
        fill="none"
      />
      <path
        d="M10,200 C200,180 380,90 590,24"
        stroke="var(--after)"
        strokeWidth="3.5"
        fill="none"
      />
    </svg>
  );
}

function Card({ eyebrow, children }: { eyebrow: string; children: ReactNode }) {
  return (
    <div className="card pad">
      <div className="eyebrow">{eyebrow}</div>
      <div style={{ marginTop: 14 }}>{children}</div>
    </div>
  );
}

function Dashboard() {
  return (
    <Page>
      <div style={{ maxWidth: 860, margin: "4px auto 30px" }}>
        <h1 style={{ fontSize: 26, fontWeight: 600, textAlign: "center", marginBottom: 16 }}>
          Good evening, Alex. What do you want to know?
        </h1>
        <div
          className="card"
          style={{ padding: "15px 19px", display: "flex", alignItems: "center", gap: 12 }}
        >
          <span className="mk" style={{ width: 22, height: 22, flex: "none" }} />
          <span style={{ flex: 1, fontSize: 16, color: "var(--ink3)" }}>
            Can I afford to take three months off next year?
          </span>
          <span className="btn">Ask</span>
        </div>
        <div
          style={{
            display: "flex",
            gap: 9,
            justifyContent: "center",
            marginTop: 14,
            flexWrap: "wrap",
          }}
        >
          {[
            "Where did my money go last month?",
            "Should I pay the card or invest?",
            "When can I stop working?",
            "What if I lose my job?",
          ].map((q) => (
            <span className="chip" key={q}>
              {q}
            </span>
          ))}
        </div>
      </div>

      <div style={{ borderTop: "1px solid var(--line)", paddingTop: 26, marginBottom: 24 }}>
        <p className="eyebrow" style={{ marginBottom: 10 }}>
          Thursday 10 September
        </p>
        <h2
          style={{
            fontSize: 27,
            lineHeight: 1.3,
            fontWeight: 600,
            maxWidth: "42ch",
            marginBottom: 7,
          }}
        >
          You&rsquo;re <span style={{ color: "var(--ok)" }}>on track</span>. Two payments are due
          this week, and clearing the card is still the fastest thing you can do.
        </h2>
        <p style={{ color: "var(--ink2)", fontSize: 15 }}>
          Nothing material has changed since last week. Your spending came in $180 under your usual.
        </p>
      </div>

      <div className="grid2">
        <div className="card pad panel">
          <div className="eyebrow">Household net worth</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 14, margin: "8px 0 2px" }}>
            <div className="big tnum">$533,903</div>
            <div style={{ color: "var(--ok)", fontSize: 14, fontWeight: 600, paddingBottom: 6 }}>
              &#9650; $4,120 this month
            </div>
          </div>
          <TwoPaths h={170} />
        </div>
        <div style={{ display: "grid", gridTemplateRows: "1fr 1fr", gap: 16 }}>
          <div
            className="card pad panel"
            style={{ display: "flex", flexDirection: "row", gap: 16, alignItems: "center" }}
          >
            {[
              ["Money in", "$13,024", "a month, after tax", undefined],
              ["Money out", "$12,024", "incl. $1,953 interest", undefined],
              ["This month", "−$180", "spending, vs that average", "var(--ok)"],
            ].map(([k, v, s, c]) => (
              <div style={{ flex: 1 }} key={k}>
                <div className="eyebrow">{k}</div>
                <div
                  className="tnum"
                  style={{ fontSize: 23, fontWeight: 700, marginTop: 6, color: c }}
                >
                  {v}
                </div>
                <div className="mut">{s}</div>
              </div>
            ))}
          </div>
          <div className="card pad panel" style={{ justifyContent: "center" }}>
            <div style={{ display: "flex", alignItems: "baseline", marginBottom: 11 }}>
              <div className="eyebrow">Where it goes</div>
              <span className="mut" style={{ marginLeft: "auto" }}>
                $1,000 saved
              </span>
            </div>
            <div
              style={{ display: "flex", height: 32, borderRadius: 7, overflow: "hidden", gap: 2 }}
            >
              {[
                [5200, "var(--ess)"],
                [1400, "var(--life)"],
                [2819, "var(--debt)"],
                [1953, "var(--warn)"],
                [1000, "var(--save)"],
              ].map(([f, c]) => (
                <div key={c} style={{ flex: f, background: c }} />
              ))}
            </div>
            <div className="legend" style={{ marginTop: 12, fontSize: 11.5 }}>
              {[
                ["Essentials", "var(--ess)"],
                ["Lifestyle", "var(--life)"],
                ["Debt", "var(--debt)"],
                ["Interest", "var(--warn)"],
                ["Saved", "var(--save)"],
              ].map(([n, c]) => (
                <span key={n}>
                  <i className="lgd" style={{ background: c }} />
                  {n}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid2 mt">
        <div className="card pad">
          <h2>Implement your plan</h2>
          <p className="mut" style={{ marginTop: 10 }}>
            A short list of moves, in order, each with the number it is worth.
          </p>
          <Bars n={5} color="var(--after)" h={110} />
        </div>
        <div className="card pad panel">
          <div className="eyebrow">Your top priority</div>
          <div style={{ fontSize: 21, fontWeight: 600, margin: "9px 0 2px" }}>
            Retiring comfortably
          </div>
          <p className="mut" style={{ marginBottom: 18 }}>
            Measured against the nest egg your plan reaches by 2053.
          </p>
          <div className="ptrow">
            <span className="plab">Your current trajectory</span>
            <span className="ptrack">
              <span className="pfill" style={{ width: "67%", background: "var(--before)" }} />
            </span>
            <span className="pval tnum">67%</span>
          </div>
          <div className="ptrow">
            <span className="plab">Abi’s plan</span>
            <span className="ptrack">
              <span className="pfill" style={{ width: "100%", background: "var(--after)" }} />
            </span>
            <span className="pval tnum">100%</span>
          </div>
        </div>
      </div>
    </Page>
  );
}

function CashFlow() {
  return (
    <Page>
      <Head title="pages.cashflow.title" sub="pages.cashflow.sub" />
      <div className="grid2 mt">
        <Card eyebrow="Money in and money out">
          <Bars n={12} color="var(--ess)" />
        </Card>
        <Card eyebrow="Where it goes">
          <Bars n={8} color="var(--life)" />
        </Card>
      </div>
    </Page>
  );
}

function NetWorth() {
  return (
    <Page>
      <Head title="pages.networth.title" sub="pages.networth.sub" />
      <div className="mt">
        <Card eyebrow="Both paths">
          <TwoPaths />
        </Card>
      </div>
    </Page>
  );
}

function Priorities() {
  return (
    <Page>
      <Head title="pages.priorities.title" sub="pages.priorities.sub" />
      <div className="mt">
        <Card eyebrow="Funded by your scores">
          {[92, 74, 51, 33].map((w) => (
            <div className="ptrow" key={w}>
              <span className="plab" />
              <span className="ptrack">
                <span className="pfill" style={{ width: `${w}%`, background: "var(--after)" }} />
              </span>
            </div>
          ))}
        </Card>
      </div>
    </Page>
  );
}

function Retirement() {
  return (
    <Page>
      <Head title="pages.retirement.title" sub="pages.retirement.sub" />
      <div className="grid2 mt">
        <Card eyebrow="Building up">
          <Bars n={14} color="var(--save)" />
        </Card>
        <Card eyebrow="Drawing down">
          <Bars n={14} color="var(--after)" />
        </Card>
      </div>
    </Page>
  );
}

function Debt() {
  return (
    <Page>
      <Head title="pages.debt.title" sub="pages.debt.sub" />
      <div className="grid2 mt">
        <Card eyebrow="Your current plan">
          <Bars n={10} color="var(--before)" />
        </Card>
        <Card eyebrow="Abi’s plan">
          <Bars n={10} color="var(--after)" />
        </Card>
      </div>
    </Page>
  );
}

function Decisions() {
  return (
    <Page>
      <Head title="pages.decisions.title" sub="pages.decisions.sub" />
      <div className="grid2 mt">
        {["A home", "A car", "A job change", "Time off"].map((n) => (
          <Card eyebrow={n} key={n}>
            <TwoPaths h={120} />
          </Card>
        ))}
      </div>
    </Page>
  );
}

export const DESK_SCREENS: Record<DeskKey, ComponentType> = {
  dashboard: Dashboard,
  "cash-flow": CashFlow,
  "net-worth": NetWorth,
  priorities: Priorities,
  retirement: Retirement,
  debt: Debt,
  decisions: Decisions,
};
